import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { List } from 'immutable';

import type { ExtendedMatchEntity } from '../types';
import type { ReduxState, ThunkDispatchType } from 'app/redux/types';

import { Scrollbar } from 'app/design-system/components/Scrollbar';
import { Spinner } from 'app/design-system/components/Spinner';
import { useTranslation } from 'app/lang';
import type { RecordOfSportsState, RecordOfTournamentsState, RecordOfUnionsState } from 'app/lib/betting/betting-entities';
import type { ChangeHandler } from 'app/widgets/Dropdown/Single';

import { addBet, resetSearchedValue } from '../actions';
import Item from '../Match/Item';
import {
  entityError,
  entityLoading,
  entityRequested,
  selectSortedMatches,
  sports,
} from '../selectors';
import SportsSelect from '../SportsSelect';
import type { SearchOption } from '../SportsSelect/DropdownComponents';
import { useSearchParams } from '../useSearchParams';

import dictionary from './Content.dictionary.json';

import styles from '../Search.module.scss';

const MIN_SEARCH_STRING_LENGTH = 3;

interface StoreProps {
  sports?: RecordOfSportsState['byId'];
  tournamentUnions?: RecordOfUnionsState['byId'];
  tournaments?: RecordOfTournamentsState['byId'];
  sortedMatches?: List<ExtendedMatchEntity>;
  error?: string;
  requested?: boolean;
  loading?: boolean;
}

interface Props extends StoreProps {
  onLinkClick: VoidFunction;
  dispatch: ThunkDispatchType;
}

const Search: React.FC<Props> = ({
  sports,
  sortedMatches,
  loading,
  error,
  requested,
  dispatch,
  onLinkClick,
}) => {
  const { t } = useTranslation();
  const { setSearchedValue, timedSearchString, searchValue } = useSearchParams();
  const isSearchStringShort = timedSearchString.length < MIN_SEARCH_STRING_LENGTH;
  const isSearchStringValid = timedSearchString.length >= MIN_SEARCH_STRING_LENGTH;
  const [activeSport, chooseSport] = useState<SearchOption>({
    label: t(dictionary.label),
    value: 0,
  });
  const [filteredMatches, setFilteredMatches] = useState<List<ExtendedMatchEntity> | null>(null);

  useEffect(() => {
    if (activeSport.value && sortedMatches) {
      const matchesBySport = sortedMatches.filter((match) => (
        match.sportId === activeSport.value
      ));

      setFilteredMatches(matchesBySport);
    }
    else if (sortedMatches && sortedMatches.size) {
      setFilteredMatches(sortedMatches);
    }
    else {
      setFilteredMatches(null);
    }
  }, [activeSport, sortedMatches]);

  useEffect(
    () => (): void => {
      dispatch(resetSearchedValue());
    },
    [],
  );

  useEffect(
    () => (): void => {
      if (isSearchStringShort && filteredMatches && filteredMatches.size) {
        dispatch(resetSearchedValue());
        setFilteredMatches(null);
      }
    },
    [filteredMatches, isSearchStringShort],
  );

  useEffect(() => {
    if (isSearchStringValid) {
      setSearchedValue(searchValue);
    }
  }, [timedSearchString]);

  const onBetClick = useCallback((matchId?: number, betId?: number) => (): void => {
    if (typeof matchId === 'number' && typeof betId === 'number') {
      dispatch(addBet(matchId, betId));
    }
  }, [addBet]);

  const handleSportChange: ChangeHandler<SearchOption> = useCallback((option) => {
    if (!option || !('value' in option)) {
      console.error('Search -> handleSportChange -> `option` not found', option);
      return;
    }

    chooseSport(option);
  }, []);

  if (isSearchStringShort) {
    return (
      <div className={styles.smallLength}>{t(dictionary.minLength)}</div>
    );
  }

  if (loading) {
    return (
      <div className={styles.loader}>
        <Spinner size={32} />
      </div>
    );
  }

  if (requested && error) {
    return (
      <div className={styles.error}>{error}</div>
    );
  }

  if (requested && !filteredMatches && isSearchStringValid) {
    return (
      <div className={styles.notFound}>
        {t(dictionary.notFound, { searchString: timedSearchString })}
      </div>
    );
  }

  return (
    <>
      {(Boolean(sports?.size)) && (
        <SportsSelect activeSport={activeSport}
                      onChange={handleSportChange} />
      )}

      <Scrollbar className={styles.scrollbars}>
        {filteredMatches && filteredMatches.map((match) => (
          <Item key={match.id}
                searchValue={timedSearchString}
                matchId={match.id}
                onLinkClick={onLinkClick}
                onBetClick={onBetClick} />
        ))}
      </Scrollbar>
      {loading && (
        <div className={styles.loader}>
          <Spinner size={32} />
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state: ReduxState): StoreProps => {
  if (!state.search || state.search.size <= 0) {
    return {};
  }

  return {
    sports: sports(state),
    error: entityError(state),
    requested: entityRequested(state),
    loading: entityLoading(state),
    sortedMatches: selectSortedMatches(state),
  };
};

export default connect(mapStateToProps)(Search);
